import { useState } from 'react';
import { Alert, AlertTitle, Button, Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Link } from 'react-router-dom';
import { SubscriptionStatus } from '@lov/shared';
import { format } from 'date-fns';
import { useVendorSubscription } from '../hooks/useVendorSubscription';

const DISMISS_KEY_PREFIX = 'sub-expiry-dismissed-';

function getDismissKey(subscriptionId: string) {
  return `${DISMISS_KEY_PREFIX}${subscriptionId}`;
}

export function SubscriptionExpiryBanner() {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only('xs'));
  const { data: subscriptionData, isLoading } = useVendorSubscription();

  const sub = subscriptionData?.subscription;
  const daysRemaining = subscriptionData?.daysRemaining ?? 0;

  const [dismissed, setDismissed] = useState(() => {
    if (!sub?.id) return false;
    return localStorage.getItem(getDismissKey(sub.id)) === '1';
  });

  if (isLoading || !sub) return null;

  const shouldShow =
    daysRemaining <= 15 &&
    daysRemaining > 0 &&
    (sub.status === SubscriptionStatus.ACTIVE || sub.status === SubscriptionStatus.TRIAL);

  if (!shouldShow) return null;

  const isCritical = daysRemaining <= 5;

  // At <= 5 days: ignore dismissal, always show
  if (!isCritical && dismissed) return null;

  const handleDismiss = () => {
    localStorage.setItem(getDismissKey(sub.id), '1');
    setDismissed(true);
  };

  const expiryDate = sub.currentPeriodEnd
    ? format(new Date(sub.currentPeriodEnd), 'MMMM d, yyyy')
    : '';

  const severity = isCritical ? 'error' : 'warning';

  return (
    <Alert
      severity={severity}
      sx={{ borderRadius: 0, mb: 0 }}
      onClose={isCritical ? undefined : handleDismiss}
      action={
        !isCritical ? (
          <Box
            component="span"
            onClick={handleDismiss}
            aria-label="dismiss"
            sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', px: 1 }}
          >
            ×
          </Box>
        ) : undefined
      }
    >
      {isXs ? (
        // Condensed mobile view
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">
            {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} left
          </Typography>
          <Button
            component={Link}
            to="/vendor/subscription/plans"
            size="small"
            variant="outlined"
            color={severity}
          >
            Renew Now
          </Button>
        </Box>
      ) : (
        // Full desktop view
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <AlertTitle>
              Subscription expiring in {daysRemaining} day{daysRemaining !== 1 ? 's' : ''}
            </AlertTitle>
            <Typography variant="body2">
              Your plan expires on {expiryDate}. Renew now to avoid service interruption.
            </Typography>
          </Box>
          <Button
            component={Link}
            to="/vendor/subscription/plans"
            size="small"
            variant="outlined"
            color={severity}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Renew Now
          </Button>
        </Box>
      )}
    </Alert>
  );
}
